import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Accordion, Flex, Progress } from 'shared';

import { PlanningSprintHeaderOutcomesContent } from 'features/planning/components/Board/Sprint/Header/Outcomes/Content';
import { SprintOutcome } from 'features/planning/types';
import { calculateOutcomeProgress } from 'features/planning/utils/calculateOutcomeProgress';

import { Typography } from 'shared/components/Typography';

type PlanningSprintHeaderOutcomesProps = {
  sprintId: string;
  title: string;
  sprintOutcomes: SprintOutcome[];
};

export const PlanningSprintHeaderOutcomes = ({
  sprintId,
  sprintOutcomes,
  title,
}: PlanningSprintHeaderOutcomesProps) => {
  const formMethods = useForm({
    defaultValues: {
      title,
      sprintOutcomes,
    },
  });

  const watchedOutcomes = formMethods.watch('sprintOutcomes');

  const outcomeProgress = calculateOutcomeProgress(watchedOutcomes || []);

  // need to reset form when props change
  useEffect(() => {
    formMethods.reset({ title, sprintOutcomes });
  }, [title, sprintOutcomes, formMethods]);

  return (
    <Accordion.Root collapsible borderTop="var(--border-secondary)">
      <Accordion.Item border="none">
        <Accordion.ItemTrigger
          px={4}
          cursor="pointer"
          justifyContent="space-between"
        >
          <Flex direction="column" gap={2} width="100%">
            <Flex justify="space-between" align="center" width="100%">
              <Typography fontSize="sm" fontWeight="medium">
                Sprint outcomes
              </Typography>
              <Accordion.ItemIndicator />
            </Flex>
            {outcomeProgress.total > 0 && (
              <Flex direction="column" gap={1} width="100%">
                <Progress
                  value={outcomeProgress.percentage}
                  size="sm"
                  colorPalette="blue"
                />
                <Typography fontSize="xs" color="gray">
                  {outcomeProgress.completed} of {outcomeProgress.total}{' '}
                  completed ({outcomeProgress.percentage}%)
                </Typography>
              </Flex>
            )}
          </Flex>
        </Accordion.ItemTrigger>
        <Accordion.ItemContent p={4} py={0}>
          <Accordion.ItemBody pb={2}>
            <FormProvider {...formMethods}>
              <PlanningSprintHeaderOutcomesContent sprintId={sprintId} />
            </FormProvider>
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  );
};
